import type { EFinancingCompany } from '@if/core/types'
import type { EDisbursementAction } from '@src/components/Disburse/disbursement-selection-schema'
import { apiClient } from '@src/services/http-common'
import type { EPaymentMethod } from '@src/types/constants'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { bankJobQueryKeys } from './bank-jobs-api'
import { pendingLenderPaymentQueryKeys } from './pending-lender-payments-api'

// POST {company}/disbursements/bank-jobs: one bank job for a selection of pending lender
// payments, all disbursed by the same method on the same date. The company goes in the route,
// where the company access check reads it, and the rest is the body.
export interface CreateBankJobRequestDto {
  financingCompanyId: EFinancingCompany
  lenderPaymentOperationIds: number[]
  paymentMethodId: EPaymentMethod
  paymentDate: string // DateOnly
  checkNumber: string | null
}

const postDisbursementBankJob = async ({ financingCompanyId, ...body }: CreateBankJobRequestDto): Promise<void> => {
  await apiClient.post(`${financingCompanyId}/disbursements/bank-jobs`, body)
}

export function useCreateBankJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: postDisbursementBankJob,
    // Disbursed payments leave the pending list. The save waits for the reload, so the form stays
    // busy until they are gone and cannot send them to the bank a second time. The new bank job
    // joins the history, which only has to be fresh the next time it is shown.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bankJobQueryKeys.all })
      return queryClient.invalidateQueries({ queryKey: pendingLenderPaymentQueryKeys.all })
    },
  })
}

export interface DisburseLenderPaymentsDto {
  action: EDisbursementAction
  lenderPaymentOperationIds: number[]
}

// TODO(US69159): APT Pay and the ACP file still need their own inputs (payment date, method
// mapping) before they can post a bank job. Until then a submit shows what would be sent.
const postDisburseLenderPayments = (dto: DisburseLenderPaymentsDto): Promise<void> => {
  window.alert(JSON.stringify(dto, null, 2))
  return Promise.resolve()
}

export function useDisburseLenderPayments() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: postDisburseLenderPayments,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pendingLenderPaymentQueryKeys.all })
      void queryClient.invalidateQueries({ queryKey: bankJobQueryKeys.all })
    },
  })
}
